import React from "react";
import { Header } from "../components/base/Header";
import { Footer } from "../components/base/Footer";
import FloatButton from "../components/base/FloatButton/FloatButton";
import Benefit2 from "../section/benefit/Benefit2/Benefit2";
import PrivateOfficeSection13 from "../section/PrivateOfficeSection13/PrivateOfficeSection13";
import "./PrivateOffice.scss";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import type { Swiper as SwiperInstance } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

type PrivateOfficeCarouselSlide = {
  id: string;
  mobileSrc: string;
  desktopSrc: string;
  alt: string;
};

type PrivateOfficeSection3Feature = {
  id: string;
  mobileBadge: string;
  desktopBadge: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
};

type PrivateOfficeSection3ImageSize = {
  mobile: {
    width: number;
    height?: number;
  };
  desktop: {
    width: number;
    height?: number;
  };
};

type PrivateOfficeSection2Concern = {
  id: string;
  badge: string;
  description: string;
  mobileDescription: string;
  image: string;
  imageAlt: string;
  desktopImageClassName: string;
  mobileImageClassName: string;
  mobileImageWrapperClassName?: string;
};

const PRIVATE_OFFICE_SECTION2_CONCERNS: PrivateOfficeSection2Concern[] = [
  {
    id: "concern-1",
    badge: "고민1",
    description: "카페에서는 눈치보이고, 집에서는 집중하기 어려워요",
    mobileDescription: "카페에서는 눈치보이고,\n집에서는 집중하기 어려워요",
    image: "/assets/private-office/section-2-img-1.png",
    imageAlt: "카페에서는 눈치보이고 집에서는 집중하기 어려운 걱정",
    desktopImageClassName:
      "w-[149px] h-[163px] ml-auto mt-auto mr-[90px] object-contain",
    mobileImageClassName: "w-[150px] h-[135px] object-contain",
  },
  {
    id: "concern-2",
    badge: "고민2",
    description: "초기 비용이 부담스럽지 않은 업무 공간을 찾고 있어요",
    mobileDescription: "초기 비용이 부담스럽지 않은\n업무 공간을 찾고 있어요",
    image: "/assets/private-office/section-2-img-2.png",
    imageAlt: "초기 비용 부담 없는 업무 공간을 찾는 걱정",
    desktopImageClassName:
      "w-[181px] h-[150px] ml-auto my-auto mr-[54px] object-contain",
    mobileImageClassName:
      "w-[164px] h-[130px] object-contain transform translate-x-[32px]",
    mobileImageWrapperClassName: "flex items-end justify-end pr-[6px]",
  },
  {
    id: "concern-3",
    badge: "고민3",
    description: "매번 무거운 짐을 들고 업무할 공간을 찾는 것이 일이에요",
    mobileDescription:
      "매번 무거운 짐을 들고 업무할\n공간을 찾는 것이 일이에요",
    image: "/assets/private-office/section-2-img-3.png",
    imageAlt: "무거운 짐을 들고 업무 공간을 찾는 걱정",
    desktopImageClassName:
      "w-[190px] h-[116px] ml-auto my-auto mr-[66px] object-contain",
    mobileImageClassName: "w-[172px] h-[112px] object-contain",
    mobileImageWrapperClassName: "flex items-end justify-end pr-[10px]",
  },
];

type PrivateOfficeServiceFeature = {
  id: string;
  order: string;
  title: string;
  image: string;
  alt: string;
};

type PrivateOfficeSection6ImageSize = {
  mobile: {
    width: number;
    height: number;
  };
  desktop: {
    width: number;
    height: number;
  };
};

const PRIVATE_OFFICE_SECTION4_SLIDES: PrivateOfficeCarouselSlide[] = Array.from(
  { length: 7 },
  (_, index) => {
    const imageIndex = index + 1;

    return {
      id: `slide-${imageIndex}`,
      mobileSrc: `/assets/private-office/carousel/mobile/${imageIndex}.png`,
      desktopSrc: `/assets/private-office/carousel/pc/${imageIndex}.png`,
      alt: `세이브오피스 동탄지점 공간 이미지 ${imageIndex}`,
    };
  }
);

const PRIVATE_OFFICE_SECTION3_FEATURES: PrivateOfficeSection3Feature[] = [
  {
    id: "feature-1",
    mobileBadge: "POINT 1",
    desktopBadge: "POINT 1",
    title: "초기 비용 0원",
    description: "인테리어, 가구 구매비 zero",
    image: "/assets/private-office/section-3-img-1.png",
    imageAlt: "초기 비용 0원 안내 이미지",
  },
  {
    id: "feature-2",
    mobileBadge: "POINT 2",
    desktopBadge: "POINT 2",
    title: "보증금 1개월",
    description: "보증금 부담 최소화",
    image: "/assets/private-office/section-3-img-2.png",
    imageAlt: "보증금 1개월 안내 이미지",
  },
  {
    id: "feature-3",
    mobileBadge: "POINT 3",
    desktopBadge: "POINT 3",
    title: "초단기 계약",
    description: "1개월 단위의 유연한 계약",
    image: "/assets/private-office/section-3-img-3.png",
    imageAlt: "초단기 계약 안내 이미지",
  },
  {
    id: "feature-4",
    mobileBadge: "POINT 4",
    desktopBadge: "POINT 4",
    title: "All-in-one",
    description: "월 이용료에 관리비, 인터넷 등 모두 포함",
    image: "/assets/private-office/section-3-img-4.png",
    imageAlt: "All-in-one 서비스 안내 이미지",
  },
];

const PRIVATE_OFFICE_SECTION3_IMAGE_SIZES: Record<
  PrivateOfficeSection3Feature["id"],
  PrivateOfficeSection3ImageSize
> = {
  "feature-1": {
    mobile: { width: 140, height: 160 },
    desktop: { width: 173, height: 198 },
  },
  "feature-2": {
    mobile: { width: 150, height: 110 },
    desktop: { width: 213, height: 156 },
  },
  "feature-3": {
    mobile: { width: 150, height: 135 },
    desktop: { width: 193, height: 174 },
  },
  "feature-4": {
    mobile: { width: 150, height: 138 },
    desktop: { width: 208, height: 191 },
  },
};

const PRIVATE_OFFICE_SECTION6_FEATURES: PrivateOfficeServiceFeature[] = [
  {
    id: "feature-1",
    order: "1",
    title: "사업자등록\n무료 대행",
    image: "/assets/private-office/section-6-img-1.png",
    alt: "사업자 등록 무료 대행 안내 이미지",
  },
  {
    id: "feature-2",
    order: "2",
    title: "365일 24시간\n쾌적한 온도",
    image: "/assets/private-office/section-6-img-2.png",
    alt: "쾌적한 온도를 유지하는 냉난방 시스템",
  },
  {
    id: "feature-3",
    order: "3",
    title: "인쇄·복사·스캔\n최신형 복합기로",
    image: "/assets/private-office/section-6-img-3.png",
    alt: "최신형 복합기 활용",
  },
  {
    id: "feature-4",
    order: "4",
    title: "무제한으로\n즐기는 커피",
    image: "/assets/private-office/section-6-img-4.png",
    alt: "무제한 제공되는 커피",
  },
  {
    id: "feature-5",
    order: "5",
    title: "끊김없이\n빠른 인터넷",
    image: "/assets/private-office/section-6-img-5.png",
    alt: "빠른 인터넷 연결",
  },
  {
    id: "feature-6",
    order: "6",
    title: "1일 1클리닝으로\n청결하게",
    image: "/assets/private-office/section-6-img-6.png",
    alt: "하루 한 번 실시되는 청소 서비스",
  },
];

const PRIVATE_OFFICE_SECTION6_IMAGE_SIZES: Record<
  PrivateOfficeServiceFeature["id"],
  PrivateOfficeSection6ImageSize
> = {
  "feature-1": {
    mobile: { width: 63, height: 45 },
    desktop: { width: 75, height: 54 },
  },
  "feature-2": {
    mobile: { width: 72, height: 38 },
    desktop: { width: 79, height: 42 },
  },
  "feature-3": {
    mobile: { width: 64, height: 64 },
    desktop: { width: 75, height: 75 },
  },
  "feature-4": {
    mobile: { width: 60, height: 60 },
    desktop: { width: 70, height: 70 },
  },
  "feature-5": {
    mobile: { width: 58, height: 58 },
    desktop: { width: 70, height: 70 },
  },
  "feature-6": {
    mobile: { width: 90, height: 90 },
    desktop: { width: 105, height: 105 },
  },
};

const PrivateOfficeSection4 = () => {
  const prevRef = React.useRef<HTMLButtonElement | null>(null);
  const nextRef = React.useRef<HTMLButtonElement | null>(null);
  const swiperRef = React.useRef<SwiperInstance | null>(null);
  const carouselImages = React.useMemo<PrivateOfficeCarouselSlide[]>(
    () => PRIVATE_OFFICE_SECTION4_SLIDES,
    []
  );

  const updateSwiperNavigation = React.useCallback(
    (swiperInstance?: SwiperInstance | null) => {
      const swiper = swiperInstance ?? swiperRef.current;

      if (!swiper || !prevRef.current || !nextRef.current) {
        return;
      }

      if (typeof swiper.params.navigation === "boolean") {
        swiper.params.navigation = {
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        };
      } else if (swiper.params.navigation) {
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.params.navigation.nextEl = nextRef.current;
      }

      if (swiper.navigation) {
        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();
      }
    },
    []
  );

  React.useEffect(() => {
    updateSwiperNavigation();
  }, [updateSwiperNavigation]);

  return (
    <div className="w-full overflow-hidden bg-gradient-to-t from-[#74B7FF] to-[#006AFF]">
      <div className="px-5 pt-[60px] pb-[60px] text-white lg:pl-[204px] lg:pr-0 lg:pt-[132px] lg:pb-[80px]">
        <div className="flex flex-col gap-y-[28px] lg:gap-y-[42px] text-center lg:grid lg:grid-cols-[1fr_auto] lg:items-end lg:gap-x-[40px] lg:text-left">
          <div className="order-1 text-[24px] font-[700] leading-[140%] lg:order-none lg:max-w-[680px] lg:text-[40px] lg:leading-[56px]">
            가격은 합리적,
            <br />
            공간은 타협하지 않았습니다.
          </div>
          <div className="order-3 flex justify-center gap-[16px] lg:order-none lg:justify-end lg:pr-[180px]">
            <button
              ref={prevRef}
              type="button"
              aria-label="이전 공간 보기"
              className="h-[42px] w-[42px] flex-shrink-0 lg:h-[54px] lg:w-[54px]"
            >
              <img
                src="/assets/private-office/carousel-arrow-left.svg"
                alt=""
                className="h-full w-full"
              />
            </button>
            <button
              ref={nextRef}
              type="button"
              aria-label="다음 공간 보기"
              className="h-[42px] w-[42px] flex-shrink-0 lg:h-[54px] lg:w-[54px]"
            >
              <img
                src="/assets/private-office/carousel-arrow-right.svg"
                alt=""
                className="h-full w-full"
              />
            </button>
          </div>
          <div className="order-2 -mr-5 overflow-hidden lg:mr-0 lg:order-none lg:col-span-2">
            <Swiper
              className=""
              modules={[Navigation]}
              slidesPerView="auto"
              loop
              observer
              observeParents
              spaceBetween={16}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                updateSwiperNavigation(swiper);
              }}
              breakpoints={{
                1024: {
                  spaceBetween: 24,
                },
              }}
            >
              {carouselImages.map((image) => (
                <SwiperSlide
                  key={image.id}
                  className="!w-[310px] lg:!w-[690px]"
                >
                  <div className="relative h-[300px] w-[310px] overflow-hidden rounded-[28px] lg:h-[500px] lg:w-[690px]">
                    <picture>
                      <source
                        media="(min-width: 1024px)"
                        srcSet={image.desktopSrc}
                      />
                      <img
                        src={image.mobileSrc}
                        alt={image.alt}
                        className="h-full w-full object-cover"
                      />
                    </picture>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivateOfficeSection5 = () => {
  return (
    <div className="w-full">
      <div className="flex flex-col items-center gap-[28px] px-5 py-[60px] text-center lg:gap-[42px] lg:px-[180px] lg:py-[132px]">
        <div className="text-[24px] font-[700] leading-[140%] lg:text-[40px] lg:leading-[56px]">
          <span className="text-[#006AFF]">동탄 1신도시 중심상권</span>
          <span className="text-[#2D343D]">
            에
            <br />
            업무 공간을 마련할 수 있습니다.
          </span>
        </div>
        <div className="flex items-center gap-[8px] rounded-full bg-[#404956] px-[16px] py-[8px] text-white lg:gap-[10px] lg:px-[26px] lg:py-[14px]">
          <img
            src="/assets/private-office/weui_location-filled.svg"
            alt=""
            className="h-[18px] w-[18px] lg:h-[26px] lg:w-[26px]"
          />
          <span className="text-[14px] font-[600] leading-[140%] lg:text-[20px] lg:leading-[28px]">
            경기 화성시 동탄중심상가1길 36
          </span>
        </div>
        <div className="w-full max-w-[355px] lg:max-w-[750px]">
          <picture>
            <source
              media="(min-width: 1026px)"
              srcSet="/assets/private-office/section-5-img.png"
            />
            <img
              src="/assets/private-office/section-5-img-mobile.png"
              alt="동탄 중심상권 편의시설 안내"
              className="h-auto w-full"
            />
          </picture>
        </div>
      </div>
    </div>
  );
};

const PrivateOfficeSection6 = () => {
  return (
    <div className="w-full bg-[#F6F7FA]">
      <div className="flex flex-col items-center gap-[28px] px-5 py-[60px] lg:gap-[42px] lg:px-[60px] lg:py-[132px] xl:px-[100px] 2xl:px-[180px]">
        <div className="text-center text-[24px] font-[700] leading-[140%] lg:text-[40px] lg:leading-[56px]">
          <span className="text-[#006AFF]">업무에 꼭 필요한 서비스</span>
          <span className="text-[#2D343D]">
            를 <br className="" />
            무료로 제공합니다.
          </span>
        </div>
        <div className="w-full max-w-[355px] lg:max-w-[1100px]">
          <div className="flex flex-wrap justify-center gap-[12px]">
            {PRIVATE_OFFICE_SECTION6_FEATURES.map((feature) => {
              const size = PRIVATE_OFFICE_SECTION6_IMAGE_SIZES[feature.id];
              const mobileWidth =
                size?.mobile.width ?? size?.desktop.width ?? 120;
              const mobileHeight =
                size?.mobile.height ?? size?.desktop.height ?? 120;
              const desktopWidth = size?.desktop.width ?? mobileWidth;
              const desktopHeight = size?.desktop.height ?? mobileHeight;
              const imageStyle = {
                "--section6-image-width-mobile": `${mobileWidth}px`,
                "--section6-image-height-mobile": `${mobileHeight}px`,
                "--section6-image-width-desktop": `${desktopWidth}px`,
                "--section6-image-height-desktop": `${desktopHeight}px`,
              } as React.CSSProperties;

              return (
                <div
                  key={feature.id}
                  className="private-office-section6__card flex w-full flex-col rounded-[24px] lg:rounded-[36px] bg-white px-[28px] pt-[24px] pb-[34px] lg:px-[42px] lg:py-[28px] lg:w-[534px]"
                >
                  <div className="private-office-section6__content flex flex-col gap-[16px] lg:flex-row lg:items-center lg:gap-[20px]">
                    <div className="private-office-section6__order flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-full bg-[#E2E6F0] text-[14px] font-[700] leading-[140%] text-[#6A7585] lg:h-[34px] lg:w-[34px] lg:text-[17px]">
                      {feature.order}
                    </div>
                    <div className="private-office-section6__info flex w-full items-center gap-[16px] lg:gap-[20px] lg:justify-between">
                      <div className="private-office-section6__title flex-1 text-left text-[18px] font-[700] leading-[145%] text-[#2D343D] whitespace-pre-line lg:text-[23px] lg:leading-[150%]">
                        {feature.title}
                      </div>
                      <div className="private-office-section6__image-wrapper flex flex-1 items-center justify-end lg:flex-none">
                        <img
                          src={feature.image}
                          alt={feature.alt}
                          className="private-office-section6__image max-w-full object-contain"
                          style={imageStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivateOfficeSection9 = () => {
  return (
    <div className="w-full bg-gradient-to-r from-[#0F73FF] to-[#4B96FF]">
      <div className="mx-auto flex w-full max-w-[1080px] flex-col items-center gap-[28px] px-5 py-[60px] text-center lg:gap-[40px] lg:px-[180px] lg:py-[80px]">
        <div className="flex flex-col items-center gap-[14px] lg:max-w-[575px]">
          <div className="text-[24px] font-[700] leading-[33.6px] text-white lg:text-[36px] lg:leading-[50.4px]">
            <span className="lg:hidden">
              목돈 지출 없는 사무실,
              <br />
              가볍게 시작하고
              <br />
              빠르게 성장하세요.
            </span>
            <span className="hidden lg:inline">
              목돈 지출 없는 사무실, 가볍게 시작하고 빠르게 성장하세요.
            </span>
          </div>
          <div className="text-[16px] font-[600] leading-[24px] text-white/60 lg:text-[22px] lg:leading-[35.2px]">
            초기 투자금 걱정은 끝.
            <span className="hidden lg:inline"> 사업에만 집중하세요.</span>
            <span className="lg:hidden">
              <br />
              사업에만 집중하세요.
            </span>
          </div>
        </div>
        <Link
          to="/consult3"
          className="inline-flex h-auto items-center justify-center rounded-full bg-white px-[20px] py-[14px] text-[17px] font-[700] leading-[24px] text-[#006AFF] lg:h-[58px] lg:w-[206px] lg:px-0 lg:text-[20px] lg:leading-[24px]"
        >
          무료 투어 신청하기
        </Link>
      </div>
    </div>
  );
};

const PrivateOffice = () => {
  return (
    <div className="private-office-page">
      <FloatButton
        message="세이브오피스로 부담없이 시작하세요."
        ctaText="무료 투어 신청하기"
        ctaPath="/consult3"
      />
      <Header ctaText="무료 투어 신청하기" ctaPath="/consult3" />
      <div className="private-office pt-[68px]">
        <div className="private-office__section private-office__section--1 px-[20px] lg:px-[40px] pt-[74px] lg:pt-[152px]">
          <div className="mx-auto w-full max-w-[1080px]">
            <div className="mx-auto flex w-full max-w-[335px] flex-col items-center text-center lg:max-w-none">
              <div className="flex flex-col items-center gap-[18px] lg:gap-0">
                <div className="whitespace-pre-line text-[31px] font-[800] leading-[41.85px] text-[#0F73FF] lg:whitespace-normal lg:text-[52px] lg:leading-[140%] lg:bg-gradient-to-r lg:from-[#0F73FF] lg:to-[#4B96FF] lg:bg-clip-text lg:text-transparent">
                  {"세이브오피스\n동탄지점 OPEN"}
                </div>
                <div className="whitespace-pre-line text-[17px] font-[600] leading-[24.65px] text-[#6A7585] lg:mt-[22px] lg:whitespace-normal lg:text-[22px] lg:leading-[160%]">
                  {"비용∙시간 부담없는 세이브오피스에서\n일에만 몰입하세요!"}
                </div>
              </div>
              <Link to="/consult3" className="mt-[40px] w-fit lg:mt-[52px]">
                <div className="rounded-full bg-[#006AFF] px-[54px] py-[16px] text-[#F9FAFB] shadow-[5.7px_5.7px_83.375px_0.3563px_rgba(224,223,229,0.8)] lg:py-[27px] lg:shadow-none">
                  <div className="text-[18px] font-[700] lg:text-[24px]">
                    무료 투어 신청하기
                  </div>
                </div>
              </Link>
            </div>
            <div className="mx-auto mt-[48px] w-full lg:relative lg:mt-[88px] lg:h-[600px] lg:w-[1080px] lg:max-w-full">
              <div className="overflow-hidden rounded-[20px] bg-[#F5F5F7] lg:hidden">
                <img
                  className="h-auto w-full rounded-[12px] object-cover"
                  src="/assets/private-office/section-1-img-mobile.png"
                  alt="세이브오피스 동탄지점 공간 미리보기"
                />
              </div>
              <div className="hidden h-full w-full overflow-hidden rounded-[30px] lg:block">
                <img
                  className="h-full w-full object-cover"
                  src="/assets/private-office/section-1-img-pc.png"
                  alt="세이브오피스 동탄지점 공간 미리보기"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="private-office__section private-office__section--2 mt-[88px] w-full px-[20px] lg:mt-[132px] lg:px-[40px]">
          <div className="mx-auto w-full max-w-[355px] lg:max-w-[1080px]">
            <div className="hidden text-[40px] font-[700] leading-[140%] text-[#2D343D] lg:block">
              이런 고민하고 계신가요?
            </div>
            <div className="flex flex-col items-center gap-[28px] py-[60px] lg:hidden">
              <div className="text-center text-[24px] font-[700] leading-[33.6px] text-[#2D343D]">
                이런 고민 하고 계신가요?
              </div>
              <div className="flex w-full flex-col gap-[10px]">
                {PRIVATE_OFFICE_SECTION2_CONCERNS.map((concern) => {
                  const mobileWrapperClassName =
                    concern.mobileImageWrapperClassName ??
                    "flex items-end justify-end";
                  const mobilePaddingClass =
                    concern.id === "concern-3"
                      ? "px-[22px] py-[32px]"
                      : "px-[28px] py-[28px]";

                  return (
                    <div
                      key={concern.id}
                      className={`flex h-[330px] flex-col gap-[12px] overflow-hidden rounded-[28px] bg-[linear-gradient(136deg,_#F6F7FA_20%,_#C3CBDF_100%)] ${mobilePaddingClass}`}
                    >
                      <div className="flex flex-col gap-[12px]">
                        <span className="inline-flex h-[32px] items-center justify-center self-start rounded-full bg-[#E2E6F0] px-[12px] text-[13px] font-[700] leading-[18.2px] text-[#6A7585]">
                          {concern.badge}
                        </span>
                        <div className="text-left text-[19px] font-[700] leading-[27.55px] text-[#2D343D] whitespace-pre-line">
                          {concern.mobileDescription}
                        </div>
                      </div>
                      <div className={`${mobileWrapperClassName} mt-auto`}>
                        <img
                          className={concern.mobileImageClassName}
                          src={concern.image}
                          alt={concern.imageAlt}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-[42px] hidden flex-col gap-y-[12px] lg:flex">
              {PRIVATE_OFFICE_SECTION2_CONCERNS.map((concern) => (
                <div
                  key={concern.id}
                  className="flex h-[200px] flex-row rounded-[36px] bg-[linear-gradient(90deg,_#F6F7FA_40.56%,_#C3CBDF_100%)] pl-[56px]"
                >
                  <div className="my-auto flex min-w-[94px] flex-col gap-y-[16px]">
                    <div className="flex h-[42px] w-fit items-center justify-center rounded-full bg-[#E2E6F0] px-[16px]">
                      <div className="text-[17px] font-[700] leading-[140%] text-[#6A7585]">
                        {concern.badge}
                      </div>
                    </div>
                    <div className="w-full text-[25px] font-[700] leading-[145%] text-[#2D343D]">
                      {concern.description}
                    </div>
                  </div>
                  <img
                    className={concern.desktopImageClassName}
                    src={concern.image}
                    alt={concern.imageAlt}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="private-office__section private-office__section--3 my-[60px] w-full px-[20px] lg:my-[132px] lg:px-[40px]">
          <div className="mx-auto w-full max-w-[355px] lg:max-w-[1080px]">
            <div className="flex flex-col items-center gap-[28px] lg:hidden">
              <div className="text-center text-[24px] font-[700] leading-[33.6px] text-[#2D343D]">
                세이브오피스는
                <br />
                <span className="text-[#006AFF]">사업의 모든 부담을 덜어</span>
                <span className="text-[#2D343D]">드립니다.</span>
              </div>
              <div className="flex w-full flex-col gap-[10px]">
                {PRIVATE_OFFICE_SECTION3_FEATURES.map((feature) => {
                  const size = PRIVATE_OFFICE_SECTION3_IMAGE_SIZES[feature.id];
                  const mobileWidth = size?.mobile.width ?? 150;
                  const mobileHeight = size?.mobile.height;
                  const mobileImageStyle: React.CSSProperties = {
                    width: `${mobileWidth}px`,
                    height: mobileHeight ? `${mobileHeight}px` : "auto",
                  };

                  return (
                    <div
                      key={feature.id}
                      className="flex flex-col gap-[24px] overflow-hidden rounded-[28px] bg-[#EAF0FF] px-[28px] py-[28px] text-left"
                    >
                      <div className="flex flex-col gap-[16px]">
                        <span className="inline-flex h-[32px] w-fit items-center justify-center self-start rounded-full bg-[#006AFF] px-[12px] text-[13px] font-[700] leading-[18.2px] text-white">
                          {feature.mobileBadge}
                        </span>
                        <div className="flex flex-col gap-[2px]">
                          <div className="text-[26px] font-[800] leading-[37.7px] text-[#0F73FF]">
                            {feature.title}
                          </div>
                          <div className="text-[16px] font-[600] leading-[23.2px] text-[#485261]">
                            {feature.description}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <img
                          src={feature.image}
                          alt={feature.imageAlt}
                          className="object-contain"
                          style={mobileImageStyle}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="hidden flex-col lg:flex">
              <div className="text-[40px] text-[#2D343D] font-[700] leading-[140%]">
                세이브오피스는
                <br />
                <span className="text-[#006AFF]">사업의 모든 부담을 덜어</span>
                드립니다.
              </div>
              <div className="mt-[42px] grid grid-cols-2 gap-[16px]">
                {PRIVATE_OFFICE_SECTION3_FEATURES.map((feature) => {
                  const size = PRIVATE_OFFICE_SECTION3_IMAGE_SIZES[feature.id];
                  const desktopWidth = size?.desktop.width ?? 200;
                  const desktopHeight = size?.desktop.height;
                  const desktopImageStyle: React.CSSProperties = {
                    width: `${desktopWidth}px`,
                    height: desktopHeight ? `${desktopHeight}px` : "auto",
                  };

                  return (
                    <div
                      key={feature.id}
                      className="flex h-[520px] flex-col rounded-[44px] bg-[#EAF0FF] p-[50px]"
                    >
                      <div className="flex h-[42px] w-fit items-center justify-center rounded-full bg-[#006AFF] px-[16px]">
                        <div className="text-[17px] font-[700] leading-[140%] text-white">
                          {feature.desktopBadge}
                        </div>
                      </div>
                      <div className="mt-[22px] bg-[linear-gradient(90deg,_#0F73FF_0%,_#4B96FF_105.41%)] bg-clip-text text-left text-[36px] font-[800] leading-[145%] text-transparent">
                        {feature.title}
                      </div>
                      <div className="mt-[4px] text-left text-[22px] font-[600] leading-[145%] text-[#485261]">
                        {feature.description}
                      </div>
                      <img
                        className="ml-auto mt-auto object-contain"
                        src={feature.image}
                        alt={feature.imageAlt}
                        style={desktopImageStyle}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="private-office__section private-office__section--4">
          <PrivateOfficeSection4 />
        </div>
        <div className="private-office__section private-office__section--5">
          <PrivateOfficeSection5 />
        </div>
        <div className="private-office__section private-office__section--6">
          <PrivateOfficeSection6 />
        </div>
        <div className="private-office__section private-office__section--7">
          <Benefit2
            hideHeaderTop
            hideHeaderSub
            renderTitle={(isMobile) =>
              isMobile ? (
                <div className="text-center text-[24px] font-[700] leading-[33.6px] text-[#2D343D]">
                  <span className="text-[#2D343D]">
                    세이브오피스 회원 전용
                    <br />
                  </span>
                  <span className="text-[#006AFF]">
                    사업자 필수 전문 서비스
                  </span>
                  <span className="text-[#2D343D]">
                    를
                    <br />
                    제공합니다.
                  </span>
                </div>
              ) : (
                <div className="text-center text-[40px] font-[700] leading-[56px] text-[#2D343D]">
                  <span className="text-[#2D343D]">
                    세이브오피스 회원 전용
                    <br />
                  </span>
                  <span className="text-[#006AFF]">
                    사업자 필수 전문 서비스
                  </span>
                  <span className="text-[#2D343D]">를 제공합니다.</span>
                </div>
              )
            }
          />
        </div>
        <div className="private-office__section private-office__section--8">
          <PrivateOfficeSection13 />
        </div>
        <div className="private-office__section private-office__section--9">
          <PrivateOfficeSection9 />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivateOffice;
